import { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import { 
  ArrowLeft, ArrowRight, Upload, 
  Instagram, Video, Linkedin, ImageIcon,
  Check, Sparkles, FileText, Loader2
} from "lucide-react";
import { Logo } from "@/components/Logo";

import { API_URL } from "../config/api";

const API = API_URL;

// Constants
const MAX_FILE_SIZE_MB = 50;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];

// Animation variants (extracted for performance)
const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -20 }
};

const fadeIn = {
  initial: { opacity: 0 },
  animate: { opacity: 1 }
};

const VideoCreator = () => {
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [loadingStep, setLoadingStep] = useState(0);
  
  // Form data
  const [projectName, setProjectName] = useState("");
  const [platform, setPlatform] = useState("");
  const [adText, setAdText] = useState("");
  const [videoDuration, setVideoDuration] = useState(12);
  const [productImage, setProductImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [createdProjectId, setCreatedProjectId] = useState(null);

  const totalSteps = 3;
  const progress = (step / totalSteps) * 100;

  const platforms = [
    { 
      id: "instagram", 
      name: "Instagram", 
      icon: <Instagram className="w-6 h-6" />,
      format: "1:1 Vierkant",
      desc: "Perfect voor Reels & Feed"
    },
    { 
      id: "tiktok", 
      name: "TikTok", 
      icon: <Video className="w-6 h-6" />,
      format: "9:16 Verticaal",
      desc: "Optimaal voor For You Page"
    },
    { 
      id: "linkedin", 
      name: "LinkedIn", 
      icon: <Linkedin className="w-6 h-6" />,
      format: "16:9 Liggend",
      desc: "Professionele B2B content"
    }
  ];

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > MAX_FILE_SIZE_BYTES) {
        toast.error(`Afbeelding mag maximaal ${MAX_FILE_SIZE_MB}MB zijn`);
        return;
      }
      if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
        toast.error("Alleen JPEG, PNG, WebP en GIF afbeeldingen zijn toegestaan");
        return;
      }
      setProductImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleNext = async () => {
    if (step === 1) {
      if (!projectName.trim()) {
        toast.error("Voer een projectnaam in");
        return;
      }
      if (!platform) {
        toast.error("Kies een platform");
        return;
      }
      setStep(2);
    } else if (step === 2) {
      if (!productImage) {
        toast.error("Upload een productfoto");
        return;
      }
      setStep(3);
    } else if (step === 3) {
      if (!adText.trim()) {
        toast.error("Voer een advertentietekst in");
        return;
      }
      await createProject();
    }
  };

  const createProject = async () => {
    setLoading(true);
    setLoadingStep(1);
    setLoadingProgress(5);
    setLoadingMessage("Project aanmaken...");
    
    try {
      // Step 1: Create project
      setLoadingStep(1);
      setLoadingProgress(10);
      const projectResponse = await axios.post(`${API}/projects`, {
        name: projectName,
        platform: platform,
        ad_text: adText,
        duration: videoDuration,
        description: "",
        has_custom_video: false
      }, {
        headers: getAuthHeaders()
      });
      
      const projectId = projectResponse.data.id;
      setCreatedProjectId(projectId);
      setLoadingProgress(20);
      
      // Step 2: Upload image
      setLoadingStep(2);
      setLoadingMessage("Foto uploaden...");
      setLoadingProgress(25);
      
      const formData = new FormData();
      formData.append("file", productImage);
      
      await axios.post(`${API}/projects/${projectId}/upload-image`, formData, {
        headers: {
          ...getAuthHeaders(),
          "Content-Type": "multipart/form-data"
        }
      });
      setLoadingProgress(35);
      
      // Step 3: Generate voice-over automatically - THIS MUST SUCCEED
      setLoadingStep(3);
      setLoadingMessage("Voice-over genereren...");
      setLoadingProgress(40);
      
      // Voice-over is REQUIRED - do not continue if it fails
      await axios.post(`${API}/projects/${projectId}/generate-voiceover`, {
        project_id: projectId,
        text: adText,
        voice_id: "21m00Tcm4TlvDq8ikWAM", // Default Rachel voice
        duration: videoDuration
      }, {
        headers: getAuthHeaders()
      });
      setLoadingProgress(50);
      
      // Step 4: Start video generation
      setLoadingStep(4);
      setLoadingMessage("Video genereren starten...");
      setLoadingProgress(55);
      
      const platformDesc = {
        instagram: "Instagram Reel advertentie",
        tiktok: "TikTok advertentie video",
        linkedin: "LinkedIn professionele advertentie"
      };
      const videoPrompt = `Maak een professionele ${platformDesc[platform] || 'social media advertentie'} video. ${adText}. De video moet aantrekkelijk en dynamisch zijn met moderne overgangen en effecten.`;
      
      await axios.post(`${API}/projects/${projectId}/generate-video`, {
        project_id: projectId,
        video_prompt: videoPrompt,
        duration: videoDuration
      }, {
        headers: getAuthHeaders()
      });
      
      setLoadingStep(5);
      setLoadingProgress(70);
      setLoadingMessage("Video wordt gegenereerd...");
      
      // Navigate to project page with generating=true to show progress view immediately
      toast.success("Video generatie gestart! Je wordt doorgestuurd...");
      await new Promise(resolve => setTimeout(resolve, 500));
      navigate(`/project/${projectId}?generating=true`);
      
    } catch (error) {
      toast.error(error.response?.data?.detail || "Kon project niet aanmaken");
    } finally {
      setLoading(false);
      setLoadingMessage("");
      setLoadingProgress(0);
      setLoadingStep(0);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 dark-mode relative overflow-hidden">
      {/* Loading Overlay */}
      <AnimatePresence>
        {loading && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[9999] bg-slate-950/95 backdrop-blur-sm flex items-center justify-center"
          >
            <div className="bg-slate-900 border border-slate-800 rounded-2xl p-8 max-w-md w-full mx-4 shadow-2xl">
              {/* Animated Icon */}
              <div className="flex justify-center mb-6">
                <div className="relative">
                  <div className="w-20 h-20 rounded-full bg-indigo-600/20 flex items-center justify-center">
                    <Loader2 className="w-10 h-10 text-indigo-500 animate-spin" />
                  </div>
                  <div className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-emerald-500 flex items-center justify-center">
                    <span className="text-xs font-bold text-white">{loadingStep}</span>
                  </div>
                </div>
              </div>
              
              {/* Progress Text */}
              <h3 className="text-xl font-semibold text-white text-center mb-2">
                {loadingMessage || "Even geduld..."}
              </h3>
              <p className="text-slate-400 text-center text-sm mb-6">
                {loadingStep === 1 && "Je project wordt aangemaakt in de database"}
                {loadingStep === 2 && "Je productfoto wordt geüpload naar de cloud"}
                {loadingStep === 3 && "AI genereert je voice-over tekst"}
                {loadingStep === 4 && "Video generatie wordt gestart met Sora 2"}
                {loadingStep === 5 && "Je wordt doorgestuurd naar je video..."}
              </p>
              
              {/* Progress Bar */}
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-400">Voortgang</span>
                  <span className="text-indigo-400 font-medium">{loadingProgress}%</span>
                </div>
                <div className="h-3 bg-slate-800 rounded-full overflow-hidden">
                  <motion.div
                    className="h-full bg-gradient-to-r from-indigo-600 to-indigo-400 rounded-full"
                    initial={{ width: 0 }}
                    animate={{ width: `${loadingProgress}%` }}
                    transition={{ duration: 0.3 }}
                  />
                </div>
              </div>
              
              {/* Steps */}
              <div className="mt-6 space-y-2">
                <div className={`flex items-center gap-3 text-sm ${loadingStep >= 1 ? 'text-white' : 'text-slate-500'}`}>
                  <div className={`w-5 h-5 rounded-full flex items-center justify-center ${loadingStep > 1 ? 'bg-emerald-500' : loadingStep === 1 ? 'bg-indigo-600 animate-pulse' : 'bg-slate-700'}`}>
                    {loadingStep > 1 ? <Check className="w-3 h-3" /> : <span className="text-xs">1</span>}
                  </div>
                  Project aanmaken
                </div>
                <div className={`flex items-center gap-3 text-sm ${loadingStep >= 2 ? 'text-white' : 'text-slate-500'}`}>
                  <div className={`w-5 h-5 rounded-full flex items-center justify-center ${loadingStep > 2 ? 'bg-emerald-500' : loadingStep === 2 ? 'bg-indigo-600 animate-pulse' : 'bg-slate-700'}`}>
                    {loadingStep > 2 ? <Check className="w-3 h-3" /> : <span className="text-xs">2</span>}
                  </div>
                  Foto uploaden
                </div>
                <div className={`flex items-center gap-3 text-sm ${loadingStep >= 3 ? 'text-white' : 'text-slate-500'}`}>
                  <div className={`w-5 h-5 rounded-full flex items-center justify-center ${loadingStep > 3 ? 'bg-emerald-500' : loadingStep === 3 ? 'bg-indigo-600 animate-pulse' : 'bg-slate-700'}`}>
                    {loadingStep > 3 ? <Check className="w-3 h-3" /> : <span className="text-xs">3</span>}
                  </div>
                  Voice-over genereren
                </div>
                <div className={`flex items-center gap-3 text-sm ${loadingStep >= 4 ? 'text-white' : 'text-slate-500'}`}>
                  <div className={`w-5 h-5 rounded-full flex items-center justify-center ${loadingStep > 4 ? 'bg-emerald-500' : loadingStep === 4 ? 'bg-indigo-600 animate-pulse' : 'bg-slate-700'}`}>
                    {loadingStep > 4 ? <Check className="w-3 h-3" /> : <span className="text-xs">4</span>}
                  </div>
                  Video genereren starten
                </div>
                <div className={`flex items-center gap-3 text-sm ${loadingStep >= 5 ? 'text-white' : 'text-slate-500'}`}>
                  <div className={`w-5 h-5 rounded-full flex items-center justify-center ${loadingStep === 5 ? 'bg-emerald-500' : 'bg-slate-700'}`}>
                    {loadingStep === 5 ? <Check className="w-3 h-3" /> : <span className="text-xs">5</span>}
                  </div>
                  Klaar!
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Subtle background */}
      <div className="absolute inset-0 noise-bg"></div>
      
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-900/90 backdrop-blur-md border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors">
            <ArrowLeft className="w-5 h-5" />
            <span>Terug naar Dashboard</span>
          </Link>
          <Logo size="md" linkTo="/dashboard" variant="light" />
        </div>
      </nav>

      {/* Progress */}
      <div className="fixed top-[73px] left-0 right-0 z-40 bg-slate-900/50 backdrop-blur-sm px-6 py-4">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-slate-400">Stap {step} van {totalSteps}</span>
            <span className="text-sm text-slate-400">{Math.round(progress)}% voltooid</span>
          </div>
          <Progress value={progress} className="h-2 bg-slate-800" />
        </div>
      </div>

      {/* Main Content */}
      <main className="pt-40 pb-40 px-6 relative z-10">
        <div className="max-w-3xl mx-auto">
          <AnimatePresence mode="wait">
            {/* Step 1: Project Details */}
            {step === 1 && (
              <motion.div
                key="step1"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
              >
                <div className="text-center mb-10">
                  <h1 className="text-3xl md:text-4xl font-bold text-white mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    Nieuwe Video Maken
                  </h1>
                  <p className="text-slate-400 text-lg">
                    Kies een platform en geef je project een naam
                  </p>
                </div>

                <Card className="bg-slate-900/80 backdrop-blur-xl border-slate-800">
                  <CardContent className="p-8 space-y-6">
                    <div className="space-y-2">
                      <Label className="text-slate-300">Projectnaam</Label>
                      <Input
                        data-testid="project-name-input"
                        placeholder="Bijv. Zomer Collectie Promo"
                        value={projectName}
                        onChange={(e) => setProjectName(e.target.value)}
                        className="h-12 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 focus:border-indigo-500"
                      />
                    </div>

                    <div className="space-y-3">
                      <Label className="text-slate-300">Kies Platform</Label>
                      <div className="grid gap-4">
                        {platforms.map((p) => (
                          <button
                            key={p.id}
                            type="button"
                            data-testid={`platform-${p.id}`}
                            onClick={() => setPlatform(p.id)}
                            className={`
                              p-4 rounded-xl border-2 transition-all duration-300 text-left flex items-center gap-4
                              ${platform === p.id 
                                ? 'border-indigo-500 bg-indigo-500/10' 
                                : 'border-slate-700 bg-slate-800/50 hover:border-slate-600'
                              }
                            `}
                          >
                            <div className={`
                              w-12 h-12 rounded-xl flex items-center justify-center
                              ${platform === p.id ? 'bg-indigo-600 text-white' : 'bg-slate-700 text-slate-400'}
                            `}>
                              {p.icon}
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <span className="font-semibold text-white">{p.name}</span>
                                <span className="text-xs text-slate-500 bg-slate-700 px-2 py-0.5 rounded">
                                  {p.format}
                                </span>
                              </div>
                              <p className="text-sm text-slate-400 mt-0.5">{p.desc}</p>
                            </div>
                            {platform === p.id && (
                              <Check className="w-5 h-5 text-indigo-400" />
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            )}

            {/* Step 2: Upload Product Photo */}
            {step === 2 && (
              <motion.div
                key="step2"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
              >
                <div className="text-center mb-10">
                  <h1 className="text-3xl md:text-4xl font-bold text-white mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    Productfoto Uploaden
                  </h1>
                  <p className="text-slate-400 text-lg">
                    Upload een foto van je product
                  </p>
                </div>

                <Card className="bg-slate-900/80 backdrop-blur-xl border-slate-800">
                  <CardContent className="p-8 space-y-6">
                    {/* Image Upload Only */}
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleImageSelect}
                      accept="image/*"
                      className="hidden"
                    />
                    
                    {!imagePreview ? (
                      <button
                        type="button"
                        data-testid="upload-image-btn"
                        onClick={() => fileInputRef.current?.click()}
                        className="w-full aspect-video rounded-xl border-2 border-dashed border-slate-700 hover:border-indigo-500 transition-colors flex flex-col items-center justify-center gap-4 bg-slate-800/50"
                      >
                        <div className="w-16 h-16 rounded-2xl bg-slate-700 flex items-center justify-center">
                          <Upload className="w-8 h-8 text-slate-400" />
                        </div>
                        <div className="text-center">
                          <p className="text-white font-medium">Klik om foto te uploaden</p>
                          <p className="text-slate-500 text-sm mt-1">PNG, JPG tot 10MB</p>
                        </div>
                      </button>
                    ) : (
                      <div className="relative">
                        <div className="aspect-video rounded-xl overflow-hidden bg-slate-800">
                          <img 
                            src={imagePreview} 
                            alt="Product preview" 
                            className="w-full h-full object-contain"
                          />
                        </div>
                        <Button
                          type="button"
                          variant="secondary"
                          data-testid="change-image-btn"
                          onClick={() => fileInputRef.current?.click()}
                          className="absolute bottom-4 right-4 bg-slate-800/90 text-white hover:bg-slate-700"
                        >
                          <ImageIcon className="w-4 h-4 mr-2" />
                          Wijzigen
                        </Button>
                      </div>
                    )}
                    <p className="text-xs text-slate-500 text-center">
                      AI genereert een video van je productfoto (4, 8 of 12 sec)
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            )}

            {/* Step 3: Ad Text */}
            {step === 3 && (
              <motion.div
                key="step3"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
              >
                <div className="text-center mb-10">
                  <h1 className="text-3xl md:text-4xl font-bold text-white mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    Advertentietekst
                  </h1>
                  <p className="text-slate-400 text-lg">
                    Schrijf de tekst voor je advertentie en voice-over
                  </p>
                </div>

                <Card className="bg-slate-900/80 backdrop-blur-xl border-slate-800">
                  <CardContent className="p-8 space-y-6">
                    <div className="space-y-2">
                      <Label className="text-slate-300 flex items-center gap-2">
                        <FileText className="w-4 h-4" />
                        Advertentietekst (Nederlands)
                      </Label>
                      <Textarea
                        data-testid="ad-text-input"
                        placeholder="Bijv. Ontdek onze nieuwe collectie! Hoogwaardige producten voor een betaalbare prijs. Bestel nu en ontvang gratis verzending."
                        value={adText}
                        onChange={(e) => setAdText(e.target.value)}
                        rows={5}
                        className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 focus:border-indigo-500 resize-none"
                      />
                      <p className="text-xs text-slate-500">
                        Deze tekst wordt gebruikt voor de voice-over en ondertitels
                      </p>
                    </div>

                    {/* Video Duration */}
                    <div className="space-y-2">
                      <Label className="text-slate-300">Video Duur</Label>
                      <Select value={String(videoDuration)} onValueChange={(val) => setVideoDuration(Number(val))}>
                        <SelectTrigger 
                          data-testid="duration-select"
                          className="bg-slate-800 border-slate-700 text-white"
                        >
                          <SelectValue placeholder="Kies video duur" />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-800 border-slate-700">
                          <SelectItem value="4" className="text-white hover:bg-slate-700">4 seconden</SelectItem>
                          <SelectItem value="8" className="text-white hover:bg-slate-700">8 seconden</SelectItem>
                          <SelectItem value="12" className="text-white hover:bg-slate-700">12 seconden (max)</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-slate-500">
                        Sora 2 ondersteunt maximaal 12 seconden per video
                      </p>
                    </div>

                    {/* Preview Summary */}
                    <div className="pt-4 border-t border-slate-800">
                      <h3 className="text-sm font-medium text-slate-400 mb-3">Samenvatting</h3>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div className="bg-slate-800/50 rounded-lg p-3">
                          <span className="text-slate-500">Project</span>
                          <p className="text-white font-medium mt-1">{projectName}</p>
                        </div>
                        <div className="bg-slate-800/50 rounded-lg p-3">
                          <span className="text-slate-500">Platform</span>
                          <p className="text-white font-medium mt-1 capitalize">{platform}</p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </main>

      {/* Bottom Navigation */}
      <div className="fixed bottom-0 left-0 right-0 bg-slate-900 border-t border-slate-800 px-6 py-5 z-[9999]">
        <div className="max-w-lg mx-auto flex items-center justify-center gap-8">
          <Button
            variant="ghost"
            onClick={() => step > 1 && setStep(step - 1)}
            disabled={step === 1}
            data-testid="prev-step-btn"
            className="text-slate-400 hover:text-white disabled:opacity-50"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Vorige
          </Button>

          <Button
            onClick={handleNext}
            disabled={loading}
            data-testid="next-step-btn"
            className="bg-indigo-600 hover:bg-indigo-700 text-white px-16 py-6 rounded-xl shadow-lg shadow-indigo-600/25 transition-all duration-300 active:scale-95 text-base font-medium"
          >
            {loading ? (
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                {loadingMessage || "Bezig..."}
              </div>
            ) : step === 3 ? (
              <>
                <Sparkles className="w-5 h-5 mr-2" />
                Project Aanmaken
              </>
            ) : (
              <>
                Volgende
                <ArrowRight className="w-5 h-5 ml-2" />
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default VideoCreator;
